import React from "react";
import mixpanelContext  from "mixpanel";
import PropTypes from "prop-types";
import Organization from "models/organization";
import ImportRequest from "./model";
import ImportRender from "./render";
import useStateMachine from "./state";

/**
 * Import Controller is a component implementing a interface for Gofast. It
 * leverages the [UploadButton](../UploadButton/README.md) to upload a Excel
 * file on S3 and then interact with the Gofast import API.
 */
    
// Limit filename length to 90 chars to leave room for prefixes.
// Total length (prefix + filename) must stay < 100 chars.
export const MAX_CHARS = 90;

export default function ImportController({
    importProcessSlug,
    importRequest,
    createImportRequest,
    refreshImportRequest,
    triggerImportRequest,
    onCancel,
    onFileSelected,
    organization,
    ...props
}) {
    // Initialize the state machine
    const [ state, transition ] = useStateMachine(importRequest);

    const mixpanel = React.useContext(mixpanelContext);
    const [openModal, setOpenModal] = React.useState(false);

    const onFileSelected_ = React.useCallback(file => {
        transition('file-selected', { file });
        if (file?.path?.length >= MAX_CHARS) {
            transition('cancel');
            setOpenModal(true);
        }
        if (onFileSelected) {
            onFileSelected(file);
        }
    }, [onFileSelected, transition]);

    const onFileUploaded = React.useCallback(key => {
        if (!key) {
            transition('cancel');
            return;
        }
        if (key?.length >= MAX_CHARS) {
            transition('cancel');
            return;}
        
        if (createImportRequest) {
            transition('file-uploaded', { key });
            createImportRequest({
                process: importProcessSlug,
                source: key,
            });
            mixpanel.track(
                'ACTION - Import',
                {
                    'Organizer ID': organization.getWeezID(),
                    'Import ID': key,
                    'Process': importProcessSlug,
                    'Status': "File Uploaded"
                }
            );
        } else {
            transition('create-import-process-wait', { key });
        }
    }, [importProcessSlug, createImportRequest, mixpanel, organization, transition]);

    const onSave = React.useCallback((details) => {
        const data = { columns: details.columns.map(target => target.target === '-' ? { target: null } : target)};
        triggerImportRequest(importRequest.getID(), {
            details: data
        });
        mixpanel.track(
            'ACTION - Import',
            {
                'Organizer ID': organization.getWeezID(),
                'Import ID': importRequest.getSource(),
                'Process': importProcessSlug,
                'Status': "Change columns"
            }
        );
    }, [importRequest, triggerImportRequest, mixpanel, importProcessSlug, organization]);

    const onTrigger = React.useMemo(() => {
        if (!triggerImportRequest) {
            return null;
        }
        return () => {
            triggerImportRequest(importRequest.getID(), {
                status: 'ready',
            });
            transition('import-process-confirmed');
            mixpanel.track(
                'ACTION - Import',
                {
                    'Organizer ID': organization.getWeezID(),
                    'Import ID': importRequest.getSource(),
                    'Process': importProcessSlug,
                    'Status': "Import"
                }
            );

        };
    }, [importRequest, triggerImportRequest, importProcessSlug, organization, transition, mixpanel]);

    const onReset = React.useCallback(() => {
        transition('cancel');
        if (onCancel) {
            onCancel();
        }
    }, [onCancel, transition]);

    React.useEffect(() => {
        if (state.step === 'create-import-process-waiting' && createImportRequest) {
            transition('create-import-process-ready');
            createImportRequest({
                process: importProcessSlug,
                source: state.key,
            });
        }
    // eslint-disable-next-line
    }, [createImportRequest]);

    // transition to import-process-changed when import process has been created
    // or when the import process has been refreshed
    React.useEffect(() => {
        if (importRequest && state.step !== 'import-process-done' && state.step !== 'import-process-confirm') {
            transition('import-process-changed', {
                importRequest,
                poll: (wait) => {
                    return setTimeout(() => {
                        refreshImportRequest(importRequest.getID());
                        transition('clear-timeout');
                    }, wait * 1000);
                },
            });
        }
    // eslint-disable-next-line
    }, [importRequest]);

    React.useEffect(() => {
        // Return the clear timeout callback
        if (state.pollingTimeoutID) {
            return () => clearTimeout(state.pollingTimeoutID);
        }
    }, [state.pollingTimeoutID]);

    return <ImportRender
        organization={ organization }
        step={ state.step }
        importRequest={ importRequest }
        onFileSelected={ onFileSelected_ }
        onFileUploaded={ onFileUploaded }
        onTrigger={ onTrigger }
        onReset={ onReset }
        onSave={ onSave }
        setOpenModal={ setOpenModal }
        openModal={ openModal }
        { ...props }
    />;
}

ImportController.propTypes = {
    /** the current organization */
    organization: PropTypes.instanceOf(Organization),
    /** the current import request */
    importRequest: PropTypes.instanceOf(ImportRequest),
    /** The slug of the import process */
    importProcessSlug: PropTypes.string.isRequired,
    /** A callback called when the user wants a new import process or stops his import process */
    onCancel: PropTypes.func,
    /** Creates the import process and sets the ImportRequest props
     *
     * @param {Object} data - the body of the creation request
     */
    createImportRequest: PropTypes.func,
    /** A callback called when a file is selected
     *
     * @param {Object} file - the selected file
     */
    onFileSelected: PropTypes.func,
    /** Refresh the import process and changes the importRequest props
     *
     * @param {string} id - the ID of the importRequest
     */
    refreshImportRequest: PropTypes.func.isRequired,
    /** Trigger the import process and changes the importRequest props
     *
     * @param {string} id - the ID of the importRequest
     * @param {object} data - the body of the trigger http request
     */
    triggerImportRequest: PropTypes.func,
    /** The title of the section */
    titles: PropTypes.object,
    /** The content of the section */
    subtitles: PropTypes.object,
    /** The warning when the user wants to leave before import */
    abortWarning: PropTypes.string,
    /** A function that mimics fetch, used for the interactions of UploadButton */
    fetch: PropTypes.func,
    /** A React Component, used to print the results once the import succeeds */
    results: PropTypes.elementType,
    /** A React Component, used to print the validation errors */
    validationError: PropTypes.elementType,
    /** The authorization URL used by the UploadButton */
    authorizationURL: PropTypes.string.isRequired,
};
